import { useMemo } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import type { TrendMeasure, TrendPoint } from '@/services/org-admin/analysis';
import { CHART_GEOMETRY, useChartTheme } from './chartTheme';
import { ChartTooltip } from './ChartTooltip';

/**
 * Spend / tokens / runs over the selected window.
 *
 * ONE measure is plotted at a time, chosen by the card's control. This is not a limitation — putting
 * spend (dollars, ~10²) and tokens (~10⁶) on one plot requires a second y-axis, and the alignment
 * between two y-scales is arbitrary, so the chart would invent a correlation that is not in the
 * data. One axis, one measure, a toggle to switch.
 *
 * A single series needs no legend: the card title and the active toggle already name it.
 */

export interface TrendAreaChartProps {
  data: TrendPoint[];
  measure: TrendMeasure;
  /** Formats the tooltip value — full precision. */
  formatValue: (value: number) => string;
  /**
   * Formats the y-axis ticks. Separate from `formatValue` because an axis has ~44px on a phone and a
   * full currency string ("$1,089.00") right-aligned in that space is CLIPPED to its tail — the axis
   * rendered as four identical "0.00" labels, which is worse than no axis at all.
   */
  formatAxis?: (value: number) => string;
  /** Human name of the measure, for the tooltip row. */
  measureLabel: string;
  /** Drives height and tick density — the panel's width, not the window's. */
  containerWidth: number | null;
}

const DAY_LABEL = new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric' });
const FULL_DATE_LABEL = new Intl.DateTimeFormat(undefined, {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});

function parseDay(iso: string): Date {
  // `T00:00:00` keeps the date in local time; bare `YYYY-MM-DD` is parsed as UTC and can render as
  // the previous day for readers west of Greenwich.
  return new Date(`${iso}T00:00:00`);
}

export function TrendAreaChart({
  data,
  measure,
  formatValue,
  formatAxis,
  measureLabel,
  containerWidth,
}: TrendAreaChartProps) {
  const theme = useChartTheme();
  const isNarrow = containerWidth !== null && containerWidth < 520;
  const height = isNarrow ? CHART_GEOMETRY.trendHeightNarrow : CHART_GEOMETRY.trendHeightWide;

  /*
   * Show at most ~6 x-axis labels regardless of window length. 30 daily labels at 11px either
   * overlap or get auto-rotated into a diagonal ramp, which is unreadable and pushes the plot up.
   * The tooltip carries the exact date for every point, so thinning labels loses nothing.
   */
  const tickInterval = useMemo(() => {
    const maxTicks = isNarrow ? 4 : 6;
    return Math.max(0, Math.ceil(data.length / maxTicks) - 1);
  }, [data.length, isNarrow]);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <AreaChart
        data={data}
        margin={{ top: 4, right: 8, bottom: 0, left: isNarrow ? -12 : 0 }}
        /*
         * Recharts' built-in keyboard layer: focuses the plot, then arrow keys walk the points and
         * announce the active one, so the tooltip's values are reachable without a pointer.
         */
        accessibilityLayer
      >
        <defs>
          <linearGradient id={`oa-trend-${measure}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={theme.magnitude} stopOpacity={0.26} />
            <stop offset="100%" stopColor={theme.magnitude} stopOpacity={0.02} />
          </linearGradient>
        </defs>

        {/* Horizontal only, solid hairline. Vertical gridlines add noise a time axis does not need,
            and dashed rules read as "threshold" when they are just a grid. */}
        <CartesianGrid stroke={theme.grid} strokeDasharray="0" vertical={false} />

        <XAxis
          dataKey="date"
          interval={tickInterval}
          tickFormatter={(iso: string) => DAY_LABEL.format(parseDay(iso))}
          tick={{ fill: theme.axis, fontSize: CHART_GEOMETRY.tickFontSize }}
          stroke={theme.grid}
          tickLine={false}
          axisLine={{ stroke: theme.grid }}
          minTickGap={8}
        />
        <YAxis
          tickFormatter={formatAxis ?? formatValue}
          tick={{ fill: theme.axis, fontSize: CHART_GEOMETRY.tickFontSize }}
          tickLine={false}
          axisLine={false}
          width={isNarrow ? 44 : 56}
          // Four ticks is enough to read a level off; more turns the plot into a grid.
          tickCount={4}
        />

        <Tooltip
          // The band is the crosshair: readers aim at a date, never at a 2px line.
          cursor={{ fill: theme.hoverBand }}
          content={({ active, payload, label }) => {
            if (!active || !payload?.length) return null;
            const value = payload[0]?.value;
            if (typeof value !== 'number') return null;
            return (
              <ChartTooltip
                title={FULL_DATE_LABEL.format(parseDay(String(label)))}
                rows={[{ label: measureLabel, value: formatValue(value), color: theme.magnitude }]}
              />
            );
          }}
        />

        <Area
          type="monotone"
          dataKey={measure}
          stroke={theme.magnitude}
          strokeWidth={2}
          fill={`url(#oa-trend-${measure})`}
          // Dots on 30 daily points is 30 marks competing with the line. The active dot appears on
          // hover/focus so the reader still sees exactly which point they are on.
          dot={false}
          activeDot={{ r: 3.5, strokeWidth: 2, stroke: theme.magnitude, fill: theme.magnitude }}
          isAnimationActive={false}
        />
      </AreaChart>
    </ResponsiveContainer>
  );
}
